import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import EmojiPicker from "emoji-picker-react";
import {
  Search,
  Pencil,
  Trash2,
  X,
  Check,
  CheckCheck,
  RotateCcw,
  Mic,
  Smile,
  Image as ImageIcon,
  Video,
  Play,
  Pause,
  Volume2,
  VolumeX,
  Square,
  Reply,
  MapPin,
} from "lucide-react";
import defaultAvatar from "../assets/profile.png";
import { Link } from "react-router-dom";
import { useSocket } from "../contexts/SocketContext";
import { normalizeMedia, SellerBadge } from "./ChatItem";
import { LocationCard, LocationViewer, ShareLocationModal, mapsLinkFor } from "./chat/ChatLocation";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
const HISTORY_LIMIT = 30;
const MAX_FILE_BYTES = 50 * 1024 * 1024;
const MAX_FILES_PER_MESSAGE = 10;
const IMAGE_TYPES = ["image/jpeg", "image/png", "image/gif"];
const VIDEO_TYPES = ["video/mp4", "video/quicktime", "video/webm"];
// Recording formats the upload API accepts (Chrome/Firefox record webm, Safari mp4)
const VOICE_TYPES = ["audio/webm", "audio/mp4"];
const MAX_MESSAGE_LENGTH = 5000;
const VOICE_MAX_SECONDS = 300;

const messageKey = (m) => (m.id ? `id_${m.id}` : `c_${m.client_id}`);

// Recently opened conversations stay in memory (this tab only, keyed by login token) so reopening one
// renders at once while the latest messages load in the background
const HISTORY_CACHE_CHATS = 20;
const HISTORY_CACHE_MESSAGES = 200;
const historyCache = new Map();
const historyCacheKey = (token, chatId, isGroup = false) => `${token}:${isGroup ? "g" : "u"}:${chatId}`;

export const clearChatHistoryCache = (token, chatId) => historyCache.delete(historyCacheKey(token, chatId));

const saveHistoryCache = (key, messages) => {
  const saved = messages.filter((m) => m.id);
  historyCache.delete(key);
  historyCache.set(key, saved.slice(-HISTORY_CACHE_MESSAGES));
  if (historyCache.size > HISTORY_CACHE_CHATS) historyCache.delete(historyCache.keys().next().value);
};

// Applies a fresh first page on top of what is on screen: cached messages inside the page's range that
// the server no longer returns were deleted meanwhile and are dropped; anything else is kept
const applyFreshPage = (prev, rows, cachedIds, complete) => {
  const freshIds = new Set(rows.map((r) => String(r.id)));
  const oldest = rows.reduce((min, r) => Math.min(min, new Date(r.created_at).getTime()), Infinity);
  const kept = prev.filter(
    (m) =>
      !m.id ||
      !cachedIds.has(String(m.id)) ||
      freshIds.has(String(m.id)) ||
      (!complete && new Date(m.created_at).getTime() < oldest)
  );
  return mergeMessages(kept, rows);
};

// Adds or replaces messages by id (or by client_id for a message still being sent), oldest first.
// Every entry matching either key is collapsed into one, so the pending bubble and the saved row
// never both stay when the socket echo and the ack arrive in either order.
const mergeMessages = (prev, incoming) => {
  let list = [...prev];
  incoming.forEach((msg) => {
    const matches = (m) =>
      (msg.id && String(m.id) === String(msg.id)) || (msg.client_id && m.client_id === msg.client_id);
    const existing = list.filter(matches);
    list = list.filter((m) => !matches(m));
    const merged = existing.reduce((acc, m) => ({ ...m, ...acc }), msg);
    list.push({ ...merged, status: msg.status });
  });
  return list.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
};

const formatTime = (value) =>
  value ? new Date(value).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : "";

const formatDay = (value) => {
  const date = new Date(value);
  const now = new Date();
  if (date.toDateString() === now.toDateString()) return "Today";
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
  return date.toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" });
};

const formatDuration = (seconds) => {
  const total = Number.isFinite(seconds) && seconds > 0 ? Math.round(seconds) : 0;
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
};

// Stable pseudo-random bar heights (percent) per voice note, so the waveform does not change between renders
const WAVE_BARS = 26;
const waveformFor = (url) => {
  let h = 0;
  for (let i = 0; i < url.length; i += 1) h = (h * 31 + url.charCodeAt(i)) >>> 0;
  return Array.from({ length: WAVE_BARS }, () => {
    h = (h * 1103515245 + 12345) >>> 0;
    return 25 + (h % 75);
  });
};

const AUDIO_PLAY_EVENT = "chat-audio-play";

const VoicePlayer = ({ url }) => {
  const audioRef = useRef(null);
  const fixingDurationRef = useRef(false);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(false);
  const bars = useMemo(() => waveformFor(url), [url]);

  // Only one voice note plays at a time
  useEffect(() => {
    const onOtherPlay = (e) => {
      if (e.detail !== audioRef.current) audioRef.current?.pause();
    };
    window.addEventListener(AUDIO_PLAY_EVENT, onOtherPlay);
    return () => window.removeEventListener(AUDIO_PLAY_EVENT, onOtherPlay);
  }, []);

  // Recorded webm files often report an Infinity duration until the end is read once
  const handleLoadedMetadata = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (Number.isFinite(audio.duration)) {
      setDuration(audio.duration);
      return;
    }
    fixingDurationRef.current = true;
    audio.currentTime = 1e101;
  };

  const handleTimeUpdate = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (fixingDurationRef.current) {
      fixingDurationRef.current = false;
      audio.currentTime = 0;
      if (Number.isFinite(audio.duration)) setDuration(audio.duration);
      return;
    }
    setCurrent(audio.currentTime);
  };

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      window.dispatchEvent(new CustomEvent(AUDIO_PLAY_EVENT, { detail: audio }));
      audio.play().catch(() => {});
    } else {
      audio.pause();
    }
  };

  const seek = (e) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    audio.currentTime = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1) * duration;
  };

  const progress = duration ? current / duration : 0;

  return (
    <div className="flex items-center gap-2 bg-[#C9F2D0] border border-[#9FDDAA] rounded-full pl-1 pr-3 py-1 w-[230px] max-w-full">
      <audio
        ref={audioRef}
        src={url}
        preload="metadata"
        muted={muted}
        onLoadedMetadata={handleLoadedMetadata}
        onTimeUpdate={handleTimeUpdate}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false);
          setCurrent(0);
        }}
        className="hidden"
      />
      <button
        type="button"
        onClick={toggle}
        className="w-7 h-7 shrink-0 rounded-full bg-[#095228] text-white flex items-center justify-center"
        aria-label={playing ? "Pause voice message" : "Play voice message"}
      >
        {playing ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5 ml-0.5" />}
      </button>
      <div className="flex-1 flex items-center gap-[2px] h-6 cursor-pointer" onClick={seek} role="presentation">
        {bars.map((height, i) => (
          <span
            key={i}
            className={`flex-1 rounded-full ${i / WAVE_BARS < progress ? "bg-[#095228]" : "bg-[#095228]/40"}`}
            style={{ height: `${height}%` }}
          />
        ))}
      </div>
      <span className="text-[11px] text-[#095228] tabular-nums">
        {formatDuration(playing || current ? current : duration)}
      </span>
      <button
        type="button"
        onClick={() => setMuted((m) => !m)}
        className="text-[#095228]"
        aria-label={muted ? "Unmute" : "Mute"}
      >
        {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
      </button>
    </div>
  );
};

// Images and videos in a grid (2 columns when there are several), voice notes as a player, a shared location as a
// map card, other files as a link
const MessageMedia = ({ media, onOpen, onOpenLocation }) => {
  const items = normalizeMedia(media);
  if (!items.length) return null;
  const visual = items.filter((item) => item.type === "image" || item.type === "video");
  const others = items.filter((item) => item.type !== "image" && item.type !== "video");

  return (
    <div className="space-y-1 mb-1">
      {visual.length > 0 && (
        <div className={`grid gap-1.5 ${visual.length > 1 ? "grid-cols-2 w-[260px] max-w-full" : "max-w-[260px]"}`}>
          {visual.map((item, i) => (
            <button
              type="button"
              key={`${item.url}_${i}`}
              onClick={() => onOpen(visual, i)}
              className="relative block overflow-hidden rounded-lg bg-black/5"
            >
              {item.type === "image" ? (
                <img
                  src={item.url}
                  alt="attachment"
                  className={`w-full object-cover ${visual.length > 1 ? "h-28" : "max-h-64"}`}
                />
              ) : (
                <>
                  <video
                    src={item.url}
                    preload="metadata"
                    muted
                    className={`w-full object-cover bg-black ${visual.length > 1 ? "h-28" : "max-h-64"}`}
                  />
                  <span className="absolute inset-0 flex items-center justify-center">
                    <span className="w-10 h-10 rounded-full bg-black/60 text-white flex items-center justify-center">
                      <Play className="w-5 h-5 ml-0.5" />
                    </span>
                  </span>
                </>
              )}
            </button>
          ))}
        </div>
      )}
      {others.map((item, i) =>
        item.type === "location" ? (
          <LocationCard key={`${item.url}_${i}`} item={item} onOpen={onOpenLocation} />
        ) : item.type === "audio" ? (
          <VoicePlayer key={`${item.url}_${i}`} url={item.url} />
        ) : (
          <a
            key={`${item.url}_${i}`}
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            className="block underline text-[#095228] break-all"
          >
            📎 Open file
          </a>
        )
      )}
    </div>
  );
};

const Lightbox = ({ items, index, onClose, onIndexChange }) => {
  const item = items[index];

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight" && index < items.length - 1) onIndexChange(index + 1);
      else if (e.key === "ArrowLeft" && index > 0) onIndexChange(index - 1);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [index, items.length, onClose, onIndexChange]);

  if (!item) return null;
  return (
    <div className="fixed inset-0 z-[1000] bg-black/85 flex items-center justify-center p-4" onClick={onClose}>
      <button onClick={onClose} className="absolute top-4 right-4 text-white" aria-label="Close">
        <X className="w-7 h-7" />
      </button>
      {index > 0 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onIndexChange(index - 1);
          }}
          className="absolute left-4 text-white text-4xl px-2"
          aria-label="Previous"
        >
          ‹
        </button>
      )}
      <div onClick={(e) => e.stopPropagation()} className="max-w-full max-h-full">
        {item.type === "video" ? (
          <video src={item.url} controls autoPlay className="max-w-[90vw] max-h-[85vh] rounded-lg" />
        ) : (
          <img src={item.url} alt="attachment" className="max-w-[90vw] max-h-[85vh] rounded-lg object-contain" />
        )}
      </div>
      {index < items.length - 1 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onIndexChange(index + 1);
          }}
          className="absolute right-4 text-white text-4xl px-2"
          aria-label="Next"
        >
          ›
        </button>
      )}
    </div>
  );
};

const senderFirstName = (sender) =>
  (sender?.first_name || "").trim() || (sender?.username || "").trim() || "Member";

// What a quoted message reads as in a reply: its text, or a label for what it carries
const QUOTE_LABELS = { image: "Photo", video: "Video", audio: "Voice message", location: "Location", file: "File" };
const quoteText = (reply) => {
  const text = (reply?.message || "").trim();
  if (text) return text;
  return QUOTE_LABELS[reply?.media_type] || (reply?.media_type ? "Photo" : "Message");
};

const escapeRegExp = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Message text with every case-insensitive match of the search term highlighted
const HighlightedText = ({ text, term }) => {
  if (!term) return text;
  const parts = text.split(new RegExp(`(${escapeRegExp(term)})`, "gi"));
  return parts.map((part, i) =>
    part.toLowerCase() === term.toLowerCase() ? (
      <mark key={i} className="bg-yellow-200 text-inherit rounded px-0.5">
        {part}
      </mark>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    )
  );
};

const ChatScreen = ({ chat, isGroup = false, keepGroupRoom = false, isOnline, onNameClick, onViewInfo, onMediaChange, onChatUpdate }) => {
  const { socket, token, currentUser, isConnected, emitWithAck, emitEvent } = useSocket();
  const me = Number(currentUser?.id);
  const chatId = chat?.id;
  const isBlockedByMe = !isGroup && chat?.myStatus === "blocked";
  const partnerFirstName = chat?.asStore ? chat?.name : (chat?.name || "").split(" ")[0];

  const cacheKey = historyCacheKey(token, chatId, isGroup);
  const [cachedAtMount] = useState(() => historyCache.get(cacheKey) || null);
  const [messages, setMessages] = useState(() => cachedAtMount || []);
  const [loading, setLoading] = useState(!cachedAtMount);
  const [loadError, setLoadError] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [input, setInput] = useState("");
  const [editing, setEditing] = useState(null);
  // The message the next one answers (shown above the input until it is sent or cancelled)
  const [replyingTo, setReplyingTo] = useState(null);
  const [menuFor, setMenuFor] = useState(null);
  const [search, setSearch] = useState("");
  const [partnerTyping, setPartnerTyping] = useState(false);
  const [typingName, setTypingName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [actionError, setActionError] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recordSeconds, setRecordSeconds] = useState(0);
  const [lightbox, setLightbox] = useState(null);
  const [sharingLocation, setSharingLocation] = useState(false);
  const [viewingLocation, setViewingLocation] = useState(null);

  const listRef = useRef(null);
  const inputRef = useRef(null);
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const emojiRef = useRef(null);
  const stickToBottomRef = useRef(true);
  const restoreScrollRef = useRef(null);
  const partnerTypingTimerRef = useRef(null);
  const typingSentAtRef = useRef(0);
  const stopTypingTimerRef = useRef(null);
  const recorderRef = useRef(null);
  const recordTimerRef = useRef(null);
  const discardRecordingRef = useRef(false);

  const isInConversation = useCallback(
    (msg) => {
      if (!msg) return false;
      if (isGroup) return !!msg.is_group_chat && Number(msg.reciever_id) === Number(chatId);
      return (
        !msg.is_group_chat &&
        ((Number(msg.sender_id) === me && Number(msg.reciever_id) === Number(chatId)) ||
          (Number(msg.sender_id) === Number(chatId) && Number(msg.reciever_id) === me))
      );
    },
    [me, chatId, isGroup]
  );

  const fetchPage = useCallback(
    async (pageToLoad) => {
      const { data } = await axios.post(
        `${API_BASE_URL}/group_chat/get-group_chats`,
        {
          filters: { reciever_id: chatId, is_group_chat: isGroup, is_deleted: false },
          page: pageToLoad,
          limit: HISTORY_LIMIT,
        },
        { headers: { token } }
      );
      const rows = data?.result?.group_chats || [];
      const total = Number(data?.result?.total) || 0;
      setPage(pageToLoad);
      setHasMore(rows.length === HISTORY_LIMIT && pageToLoad * HISTORY_LIMIT < total);
      return rows;
    },
    [chatId, token, isGroup]
  );

  const markRead = useCallback(() => {
    if (!chatId) return;
    emitWithAck("mark-read", isGroup ? { group_id: chatId } : { sender_id: chatId }).catch(() => {});
  }, [chatId, isGroup, emitWithAck]);

  // Group conversations arrive through the group's room (membership is checked by the server on join)
  useEffect(() => {
    if (!socket || !isGroup || !chatId) return;
    const join = () => socket.emit("join-group", chatId);
    if (socket.connected) join();
    socket.on("connect", join);
    return () => {
      socket.off("connect", join);
      if (!keepGroupRoom) socket.emit("leave-group", chatId);
    };
  }, [socket, isGroup, chatId, keepGroupRoom]);

  const markReadRef = useRef(markRead);
  useEffect(() => {
    markReadRef.current = markRead;
  }, [markRead]);

  // Conversation history from the database (shown from the cache first when this chat was opened before)
  useEffect(() => {
    if (!chatId || !token) return;
    let cancelled = false;
    const cachedIds = new Set((cachedAtMount || []).map((m) => String(m.id)));
    if (!cachedAtMount) setLoading(true);
    setLoadError("");
    fetchPage(1)
      .then((rows) => {
        if (cancelled) return;
        stickToBottomRef.current = true;
        setMessages((prev) => applyFreshPage(prev, rows, cachedIds, rows.length < HISTORY_LIMIT));
      })
      .catch((err) => {
        if (cancelled) return;
        const message = err.response?.data?.message || "Failed to load messages.";
        if (cachedAtMount) setActionError(message);
        else setLoadError(message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [chatId, token, fetchPage, cachedAtMount]);

  useEffect(() => {
    if (chatId && token) saveHistoryCache(cacheKey, messages);
  }, [cacheKey, chatId, token, messages]);

  // Mark as read once connected (and again after a reconnect)
  useEffect(() => {
    if (isConnected && !loading) markReadRef.current();
  }, [isConnected, loading]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") markReadRef.current();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);

  // Live updates for this conversation
  useEffect(() => {
    if (!socket) return;

    const onReceive = (msg) => {
      if (!isInConversation(msg)) return;
      setMessages((prev) => mergeMessages(prev, [msg]));
      if (isGroup ? Number(msg.sender_id) !== me : Number(msg.sender_id) === Number(chatId)) {
        setPartnerTyping(false);
        if (document.visibilityState === "visible") markReadRef.current();
      }
    };
    const onUpdate = (msg) => {
      if (!isInConversation(msg)) return;
      setMessages((prev) => prev.map((m) => (m.id === msg.id ? { ...m, ...msg } : m)));
    };
    const onDelete = ({ id } = {}) => {
      setMessages((prev) => prev.filter((m) => m.id !== id));
    };
    const onTyping = ({ isTyping, sender, is_group_chat, reciever_id } = {}) => {
      if (!sender || Number(sender.id) === me) return;
      if (isGroup ? !is_group_chat || Number(reciever_id) !== Number(chatId) : is_group_chat || Number(sender.id) !== Number(chatId)) return;
      setTypingName(senderFirstName(sender));
      setPartnerTyping(!!isTyping);
      clearTimeout(partnerTypingTimerRef.current);
      if (isTyping) partnerTypingTimerRef.current = setTimeout(() => setPartnerTyping(false), 5000);
    };
    const onRead = ({ reader_id, sender_id } = {}) => {
      if (isGroup || Number(reader_id) !== Number(chatId) || Number(sender_id) !== me) return;
      setMessages((prev) => prev.map((m) => (Number(m.sender_id) === me ? { ...m, is_read: true } : m)));
    };

    socket.on("receive-message", onReceive);
    socket.on("update-message", onUpdate);
    socket.on("delete-message", onDelete);
    socket.on("typing", onTyping);
    socket.on("messages-read", onRead);
    return () => {
      socket.off("receive-message", onReceive);
      socket.off("update-message", onUpdate);
      socket.off("delete-message", onDelete);
      socket.off("typing", onTyping);
      socket.off("messages-read", onRead);
      clearTimeout(partnerTypingTimerRef.current);
    };
  }, [socket, chatId, me, isGroup, isInConversation]);

  // Keep the list pinned to the newest message, or keep the position when older messages are added on top
  useLayoutEffect(() => {
    const el = listRef.current;
    if (!el) return;
    if (restoreScrollRef.current !== null) {
      el.scrollTop = el.scrollHeight - restoreScrollRef.current;
      restoreScrollRef.current = null;
    } else if (stickToBottomRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages, partnerTyping, loading]);

  const loadOlder = async () => {
    if (!hasMore || loadingOlder || loading) return;
    setLoadingOlder(true);
    try {
      const rows = await fetchPage(page + 1);
      restoreScrollRef.current = listRef.current ? listRef.current.scrollHeight : null;
      setMessages((prev) => mergeMessages(prev, rows));
    } catch (err) {
      setActionError(err.response?.data?.message || "Failed to load older messages.");
    } finally {
      setLoadingOlder(false);
    }
  };

  const handleScroll = (e) => {
    const el = e.currentTarget;
    stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    if (el.scrollTop < 60 && !search) loadOlder();
  };

  // Shared media for the profile panel, newest first
  useEffect(() => {
    if (!onMediaChange) return;
    const media = [];
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].id) {
        normalizeMedia(messages[i].media_url).forEach((item) => {
          if (item.type === "image" || item.type === "video") media.push(item);
        });
      }
    }
    onMediaChange(media);
  }, [messages, onMediaChange]);

  // Keep the chat list preview in step after edits and deletes
  const lastSaved = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) if (messages[i].id) return messages[i];
    return null;
  }, [messages]);
  const lastSavedKey = lastSaved ? `${lastSaved.id}|${lastSaved.message || ""}` : "";
  const lastSavedRef = useRef(lastSaved);
  lastSavedRef.current = lastSaved;
  useEffect(() => {
    if (loading || !onChatUpdate || !chatId) return;
    const last = lastSavedRef.current;
    if (!last && chat?.isNew) return;
    onChatUpdate(chatId, {
      lastMessage: last?.message || "",
      lastMessageMedia: last?.media_url,
      lastMessageAt: last?.created_at,
      lastSenderId: last?.sender_id,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastSavedKey, loading]);

  const stopTyping = useCallback(() => {
    clearTimeout(stopTypingTimerRef.current);
    if (typingSentAtRef.current) {
      emitEvent("typing", { reciever_id: chatId, is_group_chat: isGroup, isTyping: false });
      typingSentAtRef.current = 0;
    }
  }, [emitEvent, chatId, isGroup]);

  const stopTypingRef = useRef(stopTyping);
  useEffect(() => {
    stopTypingRef.current = stopTyping;
  }, [stopTyping]);

  // Leaving the chat: stop the typing indicator and drop any recording in progress
  useEffect(
    () => () => {
      stopTypingRef.current();
      clearInterval(recordTimerRef.current);
      if (recorderRef.current && recorderRef.current.state !== "inactive") {
        discardRecordingRef.current = true;
        recorderRef.current.stop();
      }
    },
    []
  );

  // At most one "typing" event every 2.5s while typing, and one "stopped" event after 2s idle
  const notifyTyping = () => {
    if (isBlockedByMe || editing) return;
    const now = Date.now();
    if (now - typingSentAtRef.current > 2500) {
      emitEvent("typing", { reciever_id: chatId, is_group_chat: isGroup, isTyping: true });
      typingSentAtRef.current = now;
    }
    clearTimeout(stopTypingTimerRef.current);
    stopTypingTimerRef.current = setTimeout(stopTyping, 2000);
  };

  const sendMessage = async ({ text = "", media = [], reply = replyingTo, replyId = null }) => {
    const client_id = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const reply_to_id = replyId || reply?.id || null;
    // Shown at once; the saved message (with the server's own copy of the quote) replaces it
    const reply_to = reply
      ? {
          id: reply.id,
          sender_id: reply.sender_id,
          sender_name:
            reply.sender_name ||
            (Number(reply.sender_id) === me ? "You" : isGroup ? senderFirstName(reply.sender) : chat?.name || "User"),
          message: (reply.message || "").slice(0, 200),
          media_type: reply.media_type || normalizeMedia(reply.media_url)[0]?.type || null,
        }
      : null;
    const pending = {
      client_id,
      sender_id: me,
      reciever_id: chatId,
      is_group_chat: isGroup,
      message: text,
      media_url: media,
      reply_to,
      created_at: new Date().toISOString(),
      status: "sending",
    };
    stickToBottomRef.current = true;
    setMessages((prev) => mergeMessages(prev, [pending]));
    setReplyingTo(null);
    try {
      const res = await emitWithAck("add-message", {
        reciever_id: chatId,
        is_group_chat: isGroup,
        message: text,
        media_url: media,
        client_id,
        ...(reply_to_id ? { reply_to_id } : {}),
      });
      if (res.payload) setMessages((prev) => mergeMessages(prev, [{ ...res.payload, client_id }]));
    } catch (err) {
      setMessages((prev) =>
        prev.map((m) => (m.client_id === client_id && !m.id ? { ...m, status: "failed", error: err.message } : m))
      );
    }
  };

  const retryMessage = (msg) => {
    setMessages((prev) => prev.filter((m) => m.client_id !== msg.client_id));
    sendMessage({ text: msg.message || "", media: msg.media_url || [], reply: null, replyId: msg.reply_to?.id });
  };

  const cancelEdit = () => {
    setEditing(null);
    setInput("");
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || isBlockedByMe) return;
    if (text.length > MAX_MESSAGE_LENGTH) {
      setActionError(`Message is too long (max ${MAX_MESSAGE_LENGTH} characters).`);
      return;
    }
    setActionError("");
    setShowEmoji(false);
    if (editing) {
      if (text === (editing.message || "").trim()) {
        cancelEdit();
        return;
      }
      try {
        const res = await emitWithAck("edit-message", { messageId: editing.id, message: text });
        if (res.message) setMessages((prev) => prev.map((m) => (m.id === editing.id ? { ...m, ...res.message } : m)));
        cancelEdit();
      } catch (err) {
        setActionError(err.message);
      }
      return;
    }
    setInput("");
    stopTyping();
    sendMessage({ text });
  };

  const startReply = (msg) => {
    setMenuFor(null);
    setEditing(null);
    setReplyingTo(msg);
    inputRef.current?.focus();
  };

  // Brings the quoted message into view (it may be further up than what is loaded)
  const goToMessage = (id) => {
    const el = document.getElementById(`msg-${id}`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.add("bg-yellow-100");
    setTimeout(() => el.classList.remove("bg-yellow-100"), 1500);
  };

  const startEdit = (msg) => {
    setMenuFor(null);
    setReplyingTo(null);
    setEditing(msg);
    setInput(msg.message || "");
    inputRef.current?.focus();
  };

  const deleteMessage = async (msg, forEveryone) => {
    setMenuFor(null);
    setActionError("");
    if (!msg.id) {
      setMessages((prev) => prev.filter((m) => m.client_id !== msg.client_id));
      return;
    }
    const question = forEveryone ? "Delete this message for everyone?" : "Delete this message for you?";
    if (!window.confirm(question)) return;
    try {
      await emitWithAck("delete-message", { messageId: msg.id, is_delete_for_all: forEveryone });
      setMessages((prev) => prev.filter((m) => m.id !== msg.id));
      if (editing?.id === msg.id) cancelEdit();
    } catch (err) {
      setActionError(err.message);
    }
  };

  // Uploads files straight to S3 through pre-signed URLs, then sends them as one message
  const uploadAndSend = async (files) => {
    setUploading(true);
    setActionError("");
    try {
      const { data } = await axios.post(
        `${API_BASE_URL}/aws/generate-presigned-url`,
        { files: files.map(({ file, name }) => ({ fileName: name || file.name, fileType: file.type })), type: "uploads" },
        { headers: { token } }
      );
      const targets = data?.directory || [];
      if (targets.length !== files.length || targets.some((t) => !t?.presignedUrl || !t?.fileUrl)) {
        throw new Error("Upload could not be started.");
      }
      await Promise.all(
        targets.map((target, i) =>
          axios.put(target.presignedUrl, files[i].file, { headers: { "Content-Type": files[i].file.type } })
        )
      );
      await sendMessage({ media: targets.map((target, i) => ({ url: target.fileUrl, media_type: files[i].mediaType })) });
    } catch (err) {
      setActionError(err.response?.data?.message || err.message || "Failed to send the file.");
    } finally {
      setUploading(false);
    }
  };

  const sendLocation = (coords) => {
    setSharingLocation(false);
    setActionError("");
    const latitude = Number(coords.latitude.toFixed(6));
    const longitude = Number(coords.longitude.toFixed(6));
    sendMessage({ media: [{ media_type: "location", latitude, longitude, url: mapsLinkFor(latitude, longitude) }] });
  };

  const closeLocation = useCallback(() => setSharingLocation(false), []);
  const closeLocationViewer = useCallback(() => setViewingLocation(null), []);

  const handlePickFiles = (allowedTypes, mediaType, label) => (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    if (!picked.length) return;
    if (picked.length > MAX_FILES_PER_MESSAGE) {
      setActionError(`You can send up to ${MAX_FILES_PER_MESSAGE} files at a time.`);
      return;
    }
    if (picked.some((file) => !allowedTypes.includes(file.type))) {
      setActionError(`Unsupported file type. Please choose ${label}.`);
      return;
    }
    if (picked.some((file) => file.size > MAX_FILE_BYTES)) {
      setActionError("Each file must be 50 MB or smaller.");
      return;
    }
    uploadAndSend(picked.map((file) => ({ file, mediaType })));
  };

  const startRecording = async () => {
    setActionError("");
    const mimeType = VOICE_TYPES.find(
      (type) => typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported?.(type)
    );
    if (!mimeType || !navigator.mediaDevices?.getUserMedia) {
      setActionError("Voice messages are not supported in this browser.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType });
      const chunks = [];
      recorderRef.current = recorder;
      discardRecordingRef.current = false;

      recorder.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      recorder.onstop = () => {
        stream.getTracks().forEach((track) => track.stop());
        clearInterval(recordTimerRef.current);
        setRecording(false);
        setRecordSeconds(0);
        if (discardRecordingRef.current || !chunks.length) return;
        const type = mimeType;
        const blob = new Blob(chunks, { type });
        if (blob.size > MAX_FILE_BYTES) {
          setActionError("Voice message is too long.");
          return;
        }
        const extension = type === "audio/mp4" ? "m4a" : "webm";
        const file = new File([blob], `voice-${Date.now()}.${extension}`, { type });
        uploadAndSend([{ file, mediaType: "audio" }]);
      };

      recorder.start();
      setRecording(true);
      setRecordSeconds(0);
      let elapsed = 0;
      recordTimerRef.current = setInterval(() => {
        elapsed += 1;
        setRecordSeconds(elapsed);
        if (elapsed >= VOICE_MAX_SECONDS && recorder.state !== "inactive") recorder.stop();
      }, 1000);
    } catch (err) {
      setActionError("Microphone access is needed to record a voice message.");
    }
  };

  const finishRecording = (discard) => {
    discardRecordingRef.current = discard;
    if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop();
  };

  const insertEmoji = (emoji) => {
    const el = inputRef.current;
    const start = el ? el.selectionStart ?? input.length : input.length;
    const end = el ? el.selectionEnd ?? input.length : input.length;
    const next = input.slice(0, start) + emoji + input.slice(end);
    setInput(next);
    requestAnimationFrame(() => {
      if (!inputRef.current) return;
      inputRef.current.focus();
      inputRef.current.setSelectionRange(start + emoji.length, start + emoji.length);
    });
  };

  // Close the message menu / emoji picker on any outside click
  useEffect(() => {
    if (!menuFor) return;
    const close = () => setMenuFor(null);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [menuFor]);

  useEffect(() => {
    if (!showEmoji) return;
    const close = (e) => {
      if (emojiRef.current && !emojiRef.current.contains(e.target)) setShowEmoji(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [showEmoji]);

  const openLightbox = useCallback((items, index) => setLightbox({ items, index }), []);
  const closeLightbox = useCallback(() => setLightbox(null), []);
  const changeLightboxIndex = useCallback((index) => setLightbox((prev) => (prev ? { ...prev, index } : prev)), []);

  const term = search.trim().toLowerCase();
  const visibleMessages = term
    ? messages.filter((m) => (m.message || "").toLowerCase().includes(term))
    : messages;

  let lastDay = "";
  const renderedMessages = visibleMessages.map((msg) => {
    const key = messageKey(msg);
    const isMine = Number(msg.sender_id) === me;
    const day = msg.created_at ? new Date(msg.created_at).toDateString() : "";
    const showDay = day && day !== lastDay;
    lastDay = day || lastDay;
    const hasText = !!(msg.message && msg.message.trim());
    const mediaItems = normalizeMedia(msg.media_url);
    const isVoiceOnly = !hasText && mediaItems.length === 1 && mediaItems[0].type === "audio";

    const meta = (
      <div className="flex items-center gap-1 text-[10px] text-gray-500 mt-1 justify-end">
        <span>{msg.status === "sending" ? "Sending..." : formatTime(msg.created_at)}</span>
        {isMine && !msg.status && (
          !isGroup && msg.is_read ? (
            <CheckCheck className="w-3.5 h-3.5 text-[#39B54A]" aria-label="Read" />
          ) : (
            <Check className="w-3.5 h-3.5" aria-label="Sent" />
          )
        )}
      </div>
    );

    return (
      <React.Fragment key={key}>
        {showDay && (
          <div className="flex justify-center">
            <span className="text-[11px] text-gray-500 bg-gray-100 rounded-full px-3 py-1">
              {formatDay(msg.created_at)}
            </span>
          </div>
        )}
        <div
          id={msg.id ? `msg-${msg.id}` : undefined}
          className={`group flex items-end gap-2 transition-colors ${isMine ? "justify-end" : "justify-start"}`}
        >
          {!isMine && (
            <img
              src={(isGroup ? msg.sender?.profile_picture_url : chat?.avatar) || defaultAvatar}
              alt={isGroup ? senderFirstName(msg.sender) : chat?.name}
              className="w-7 h-7 rounded-full object-cover shrink-0 self-start"
            />
          )}

          {msg.id && (
            <div className={`relative ${isMine ? "order-first" : "order-last"}`}>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setMenuFor(menuFor === key ? null : key);
                }}
                className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-gray-400 hover:text-gray-600 p-1"
                aria-label="Message options"
              >
                ⋮
              </button>
              {menuFor === key && (
                <div
                  onClick={(e) => e.stopPropagation()}
                  className={`absolute z-20 bottom-7 ${isMine ? "right-0" : "left-0"} bg-white border rounded-lg shadow-lg py-1 w-44 text-sm`}
                >
                  {!isBlockedByMe && (
                    <button
                      onClick={() => startReply(msg)}
                      className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-100 text-left"
                    >
                      <Reply className="w-4 h-4" /> Reply
                    </button>
                  )}
                  {isMine && hasText && (
                    <button
                      onClick={() => startEdit(msg)}
                      className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-100 text-left"
                    >
                      <Pencil className="w-4 h-4" /> Edit
                    </button>
                  )}
                  <button
                    onClick={() => deleteMessage(msg, false)}
                    className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-100 text-left"
                  >
                    <Trash2 className="w-4 h-4" /> Delete for me
                  </button>
                  {isMine && (
                    <button
                      onClick={() => deleteMessage(msg, true)}
                      className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-100 text-left text-red-600"
                    >
                      <Trash2 className="w-4 h-4" /> Delete for everyone
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {isVoiceOnly ? (
            <div className={`max-w-[75%] ${msg.status === "failed" ? "rounded-2xl border border-red-300 p-1" : ""}`}>
              {msg.reply_to && (
                <button
                  type="button"
                  onClick={() => goToMessage(msg.reply_to.id)}
                  className="block w-full text-left border-l-4 border-[#095228] bg-black/5 rounded px-2 py-1 mb-1"
                >
                  <span className="block text-xs font-semibold text-[#095228] truncate">
                    {Number(msg.reply_to.sender_id) === me ? "You" : msg.reply_to.sender_name}
                  </span>
                  <span className="block text-xs text-gray-600 line-clamp-2">{quoteText(msg.reply_to)}</span>
                </button>
              )}
              <VoicePlayer url={mediaItems[0].url} />
              {meta}
              {msg.status === "failed" && <FailedActions msg={msg} onRetry={retryMessage} onRemove={deleteMessage} />}
            </div>
          ) : (
            <div
              className={`px-3 py-2 rounded-2xl max-w-[75%] md:max-w-sm break-words whitespace-pre-wrap text-sm ${
                isMine ? "bg-[#C9F2D0] rounded-br-sm" : "bg-[#F0F0F0] rounded-tl-sm"
              } ${msg.status === "failed" ? "border border-red-300" : ""} text-[#1F1F1F]`}
            >
              {!isMine && (isGroup || partnerFirstName) && (
                <p className="text-xs font-semibold text-[#000000] mb-0.5">
                  {isGroup ? senderFirstName(msg.sender) : partnerFirstName}
                </p>
              )}
{msg.reply_to && (
                <button
                  type="button"
                  onClick={() => goToMessage(msg.reply_to.id)}
                  className="block w-full text-left border-l-4 border-[#095228] bg-black/5 rounded px-2 py-1 mb-1"
                >
                  <span className="block text-xs font-semibold text-[#095228] truncate">
                    {Number(msg.reply_to.sender_id) === me ? "You" : msg.reply_to.sender_name}
                  </span>
                  <span className="block text-xs text-gray-600 line-clamp-2">{quoteText(msg.reply_to)}</span>
                </button>
              )}
              <MessageMedia media={msg.media_url} onOpen={openLightbox} onOpenLocation={setViewingLocation} />
              {hasText && (
                <span>
                  <HighlightedText text={msg.message} term={search.trim()} />
                </span>
              )}
              {meta}
              {msg.status === "failed" && <FailedActions msg={msg} onRetry={retryMessage} onRemove={deleteMessage} />}
            </div>
          )}
        </div>
      </React.Fragment>
    );
  });

  return (
    <div className="flex flex-col h-[70vh] min-h-[460px]">
      {/* Header */}
      {isGroup ? (
        <div className="flex flex-wrap items-center gap-3 bg-white rounded-t-2xl px-3 py-2.5 border-b border-[#EDEDED]">
          <img
            src={chat?.avatar || defaultAvatar}
            alt={chat?.name}
            className="w-12 h-12 rounded-lg border border-[#E5E5E5] object-cover shrink-0"
          />
          <div className="flex-1 min-w-0 text-center">
            <h2 className="font-semibold text-lg text-[#2C2C2E] truncate">{chat?.name}</h2>
            <p className="text-[11px] text-[#666668]">
              {partnerTyping
                ? `${typingName || "Someone"} is typing...`
                : `${Number(chat?.membersCount) || 0} Members${chat?.createdAt ? ` | Created on : ${formatDay(chat.createdAt)}` : ""}`}
            </p>
            <button type="button" onClick={onViewInfo} className="text-[#095228] text-xs font-semibold underline">
              View Group Info
            </button>
          </div>
          <div className="relative w-full sm:w-56 lg:w-64 order-last sm:order-none">
            <input
              type="text"
              value={search}
              maxLength={100}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Find Message"
              className="w-full pl-9 pr-3 py-2 rounded-lg bg-[#F5F5F5] border border-[#E5E5E5] focus:outline-none focus:ring-2 focus:ring-green-400 text-sm"
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
          </div>
        </div>
      ) : (
      <div className="flex flex-wrap items-center justify-between gap-2 bg-[#EBF8ED] rounded-t-2xl px-3 py-2.5">
        <div className="flex items-center gap-3 min-w-0">
          <img
            src={chat?.avatar || defaultAvatar}
            alt={chat?.name}
            className="w-11 h-11 rounded-lg border-2 border-[#F7A440] object-cover shrink-0"
          />
          <div className="min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <button type="button" onClick={onNameClick} className="text-left min-w-0">
                <h2 className="font-semibold text-base text-[#000000] underline underline-offset-2 truncate">
                  {chat?.name}
                </h2>
              </button>
              {chat?.sellerId && (
                <>
                  <SellerBadge />
                  <Link
                    to={`/shopbuyeformarketplace?sellerId=${chat.sellerId}`}
                    className="shrink-0 text-xs font-semibold text-[#095228] underline underline-offset-2"
                  >
                    Visit Store
                  </Link>
                </>
              )}
            </div>
            <p
              className={`text-xs flex items-center ${
                partnerTyping || isOnline ? "text-[#39B54A]" : "text-gray-500"
              }`}
            >
              <span
                className={`w-1.5 h-1.5 rounded-full mr-1 ${
                  partnerTyping || isOnline ? "bg-[#39B54A]" : "bg-gray-400"
                }`}
              ></span>
              {partnerTyping ? "typing..." : isOnline ? "Online" : "Offline"}
            </p>
          </div>
        </div>

        <div className="relative">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Find Message"
            className="pl-9 pr-3 py-2 rounded-lg bg-white border border-[#E5E5E5] focus:outline-none focus:ring-2 focus:ring-green-400 text-sm w-40 sm:w-48"
          />
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
        </div>
      </div>
      )}

      {!isConnected && !loading && (
        <p className="text-center text-xs text-amber-600 mt-2">Reconnecting...</p>
      )}

      {/* Chat Messages */}
      <div ref={listRef} onScroll={handleScroll} className="flex-1 overflow-y-auto py-4 space-y-3 px-3">
        {loadingOlder && <p className="text-center text-xs text-gray-500">Loading older messages...</p>}
        {loading && <p className="text-center text-sm text-gray-500 py-6">Loading messages...</p>}
        {!loading && loadError && <p className="text-center text-sm text-red-500 py-6">{loadError}</p>}
        {!loading && !loadError && messages.length === 0 && (
          <p className="text-center text-sm text-gray-500 py-6">No messages yet. Say hello!</p>
        )}
        {!loading && term && visibleMessages.length === 0 && messages.length > 0 && (
          <p className="text-center text-sm text-gray-500 py-6">No messages match your search.</p>
        )}

        {renderedMessages}

        {partnerTyping && (
          <div className="text-xs text-gray-500 italic pl-9">
            {(isGroup ? typingName : partnerFirstName) || "User"} is typing...
          </div>
        )}
      </div>

      {actionError && <p className="text-xs text-red-500 px-4 pb-1">{actionError}</p>}
      {input.length > MAX_MESSAGE_LENGTH - 500 && (
        <p className={`text-[11px] px-4 pb-1 text-right ${input.length >= MAX_MESSAGE_LENGTH ? "text-red-500" : "text-gray-500"}`}>
          {input.length}/{MAX_MESSAGE_LENGTH}
        </p>
      )}

      {replyingTo && (
        <div className="flex items-center justify-between bg-[#EBF8ED] text-xs text-gray-600 mx-3 px-3 py-1 rounded-t-lg">
          <span className="truncate">
            Replying to{" "}
            <b>
              {Number(replyingTo.sender_id) === me
                ? "yourself"
                : replyingTo.sender_name || (isGroup ? senderFirstName(replyingTo.sender) : chat?.name || "User")}
            </b>
            : {quoteText({ message: replyingTo.message, media_type: normalizeMedia(replyingTo.media_url)[0]?.type })}
          </span>
          <button onClick={() => setReplyingTo(null)} aria-label="Cancel reply">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {editing && (
        <div className="flex items-center justify-between bg-[#EBF8ED] text-xs text-gray-600 mx-3 px-3 py-1 rounded-t-lg">
          <span className="truncate">Editing: {editing.message}</span>
          <button onClick={cancelEdit} aria-label="Cancel editing">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Composer */}
      {isBlockedByMe ? (
        <p className="border-t border-gray-200 py-3 px-3 text-sm text-center text-gray-500">
          You blocked this user. Unblock them from their profile to send messages.
        </p>
      ) : (
        <div className="relative flex items-center gap-2 px-3 py-3">
          <img
            src={currentUser?.profile_picture_url || defaultAvatar}
            alt="You"
            className="hidden sm:block w-10 h-10 rounded-lg border-2 border-[#F7A440] object-cover shrink-0"
          />

          <input
            ref={imageInputRef}
            type="file"
            multiple
            accept={IMAGE_TYPES.join(",")}
            onChange={handlePickFiles(IMAGE_TYPES, "image", "JPG, PNG or GIF images")}
            className="hidden"
          />
          <input
            ref={videoInputRef}
            type="file"
            multiple
            accept={VIDEO_TYPES.join(",")}
            onChange={handlePickFiles(VIDEO_TYPES, "video", "MP4, MOV or WEBM videos")}
            className="hidden"
          />

          <div className="flex-1 min-w-0 flex items-center gap-2 border border-[#D9D9D9] rounded-xl px-3 py-2 bg-white">
            {recording ? (
              <>
                <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse shrink-0" />
                <span className="flex-1 text-sm text-gray-700">Recording {formatDuration(recordSeconds)}</span>
                <button
                  type="button"
                  onClick={() => finishRecording(true)}
                  className="text-gray-500 hover:text-red-600"
                  aria-label="Cancel recording"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
                <button
                  type="button"
                  onClick={() => finishRecording(false)}
                  className="text-[#095228]"
                  aria-label="Stop and send voice message"
                >
                  <Square className="w-5 h-5 fill-current" />
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={startRecording}
                  disabled={uploading || !!editing}
                  className="text-gray-600 hover:text-[#095228] disabled:opacity-40 shrink-0"
                  aria-label="Record a voice message"
                >
                  <Mic className="w-5 h-5" />
                </button>
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={(e) => {
                    setInput(e.target.value);
                    notifyTyping();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    } else if (e.key === "Escape" && editing) {
                      cancelEdit();
                    }
                  }}
                  maxLength={MAX_MESSAGE_LENGTH}
                  placeholder={uploading ? "Uploading..." : "Add a comment"}
                  className="flex-1 min-w-0 text-sm bg-transparent border-none outline-none focus:ring-0 p-0"
                />
                <div ref={emojiRef} className="relative shrink-0">
                  <button
                    type="button"
                    onClick={() => setShowEmoji((v) => !v)}
                    className="text-gray-600 hover:text-[#095228] flex items-center"
                    aria-label="Add an emoji"
                  >
                    <Smile className="w-5 h-5" />
                  </button>
                  {showEmoji && (
                    <div className="absolute bottom-9 right-0 z-30">
                      <EmojiPicker
                        height={340}
                        width={300}
                        lazyLoadEmojis
                        previewConfig={{ showPreview: false }}
                        onEmojiClick={(emojiData) => insertEmoji(emojiData.emoji)}
                      />
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => imageInputRef.current?.click()}
                  disabled={uploading || !!editing}
                  className="text-[#E5484D] hover:opacity-80 disabled:opacity-40 shrink-0"
                  aria-label="Send photos"
                >
                  <ImageIcon className="w-5 h-5" />
                </button>
                <button
                  type="button"
                  onClick={() => videoInputRef.current?.click()}
                  disabled={uploading || !!editing}
                  className="text-[#39B54A] hover:opacity-80 disabled:opacity-40 shrink-0"
                  aria-label="Send videos"
                >
                  <Video className="w-5 h-5" />
                </button>
                <button
                  type="button"
                  onClick={() => setSharingLocation(true)}
                  disabled={uploading || !!editing}
                  className="text-[#08AEEA] hover:opacity-80 disabled:opacity-40 shrink-0"
                  aria-label="Share location"
                >
                  <MapPin className="w-5 h-5" />
                </button>
              </>
            )}
          </div>

          <button
            onClick={handleSend}
            disabled={!input.trim() || recording}
            className="shrink-0 px-6 py-2.5 rounded-xl text-white text-sm font-semibold bg-gradient-to-r from-[#1DE78B] to-[#08AEEA] hover:opacity-90 disabled:opacity-50"
          >
            {editing ? "Save" : "Send"}
          </button>
        </div>
      )}

      {sharingLocation && <ShareLocationModal onSend={sendLocation} onClose={closeLocation} />}
      {viewingLocation && <LocationViewer item={viewingLocation} onClose={closeLocationViewer} />}

      {lightbox && (
        <Lightbox
          items={lightbox.items}
          index={lightbox.index}
          onClose={closeLightbox}
          onIndexChange={changeLightboxIndex}
        />
      )}
    </div>
  );
};

const FailedActions = ({ msg, onRetry, onRemove }) => (
  <div className="flex items-center gap-3 text-[11px] text-red-600 mt-1">
    <span>Not sent</span>
    <button onClick={() => onRetry(msg)} className="flex items-center gap-1 underline">
      <RotateCcw className="w-3 h-3" /> Retry
    </button>
    <button onClick={() => onRemove(msg, false)} className="underline">
      Remove
    </button>
  </div>
);

export default ChatScreen;
